import { ClassValue, clsx } from 'clsx'
import { Session } from 'next-auth'
import { extendTailwindMerge } from 'tailwind-merge'

const customTwMerge = extendTailwindMerge({
  theme: {
    spacing: ['2xs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl']
  }
})

export function cn(...inputs: ClassValue[]): string {
  return customTwMerge(clsx(inputs))
}

export function isFeatureFlagLocallyEnabled(featureFlag: string): boolean {
  return process.env.NEXT_PUBLIC_LOCALLY_ENABLED_FLAGS?.split(',').includes(featureFlag) ?? false
}

export function isFeatureFlagEnabledForSession(featureFlag: string, session: Session | null): boolean {
  return session?.user?.featureFlags?.includes(featureFlag) ?? false
}

export function shortenAddress(address: string, compact = false): string {
  if (compact) {
    return `${address.slice(0, 4)}...`
  }

  return `${address.slice(0, 4)}...${address.slice(-4)}`
}

function toFixedFloor(num: number, decimalPlaces: number): string {
  const factor = Math.pow(10, decimalPlaces)
  return (Math.floor(num * factor) / factor).toFixed(decimalPlaces)
}

export function truncatePriceString(price: string, options?: { floor?: boolean; decimalPlaces?: number }): string {
  const { floor = false, decimalPlaces = 4 } = options ?? {}
  return floor ? toFixedFloor(Number(price), decimalPlaces) : Number(price).toFixed(decimalPlaces)
}

export function formatUSD(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

/**
 * Periodically checks for the existence of an element matching the given selector.
 * Once the element is found, the callback function is executed.
 *
 * @param selector - The CSS selector for the element to find.
 * @param callback - The function to execute once the element is found.
 * @param interval - The time in milliseconds between checks. Defaults to 100ms.
 * @returns A function that can be called to clear the interval and stop polling.
 */
export function pollForElement<Element extends HTMLElement>(
  selector: string,
  callback: (element: Element) => void,
  interval = 100
): () => void {
  const intervalId = setInterval(() => {
    const element = document.querySelector(selector)
    if (element) {
      callback(element as Element)
      clearInterval(intervalId)
    }
  }, interval)

  return () => {
    clearInterval(intervalId)
  }
}

export async function isPasskeySupported(): Promise<boolean> {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable !== 'undefined' &&
    (await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable())
  )
}
